import { useState } from 'react';
import axios from 'axios';
import { track } from '../../lib/analytics';
import { toast } from 'sonner';
import { Bell, Check, Loader2 } from 'lucide-react';
import { regionSlug } from '../../lib/regions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-200 inline-flex items-center gap-1 ${active ? 'bg-[#C5A059] text-[#2D1037] border-[#C5A059]' : 'bg-transparent text-current border-current/30 hover:border-[#C5A059]'}`}>
    {active && <Check size={12} />} {children}
  </button>
);

export const AlertForm = ({ options = [], initial = [], dark = false, title, subtitle, endpoint, listField, anyField, anyLabel, testPrefix, doneText }) => {
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState(new Set(initial));
  const [anyTown, setAnyTown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const toggle = (r) => setSelected((s) => { const n = new Set(s); n.has(r) ? n.delete(r) : n.add(r); return n; });
  const submit = async (e) => {
    e.preventDefault();
    if (!anyTown && selected.size === 0) { toast.error(`Choose at least one option or ${anyLabel}`); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${endpoint}`, { email, [listField]: [...selected], [anyField]: anyTown });
      track('sign_up', { method: endpoint.replace(/\//g, '_') });
      setDone(r.data);
      toast.success("You're on the list");
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please enter a valid email'); }
    setBusy(false);
  };

  const shell = dark ? 'bg-[#2D1037] text-[#FAF7F2] border-[#C5A059]/30' : 'bg-[#F5EFE9] text-[#1E1622] border-[#E6DAC5]';
  if (done) {
    return (
      <div className={`rounded-2xl border p-6 ${shell}`} data-testid={`${testPrefix}-success`}>
        <p className="font-display text-xl mb-1 flex items-center gap-2"><Check size={18} className="text-[#C5A059]" /> You're on the list</p>
        <p className="text-sm opacity-70">{doneText(done)}</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className={`rounded-2xl border p-6 ${shell}`} data-testid={`${testPrefix}-form`}>
      <p className="font-display text-xl mb-1 flex items-center gap-2"><Bell size={18} className="text-[#C5A059]" /> {title}</p>
      <p className="text-sm opacity-70 mb-4">{subtitle}</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <Chip active={anyTown} onClick={() => setAnyTown((v) => !v)} testId={`${testPrefix}-any`}>{anyLabel}</Chip>
        {options.map((r) => (
          <Chip key={r} active={selected.has(r)} onClick={() => toggle(r)} testId={`${testPrefix}-region-${regionSlug(r)}`}>{r}</Chip>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
          data-testid={`${testPrefix}-email`} className="flex-grow rounded-full px-5 py-3 text-sm text-[#1E1622] bg-white border border-[#E6DAC5] focus:outline-none focus:border-[#C5A059]" />
        <button type="submit" disabled={busy} data-testid={`${testPrefix}-submit`} className="btn-gold text-sm inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} Notify me
        </button>
      </div>
    </form>
  );
};

export const TownAlertForm = ({ regions = [], initialRegions = [], dark = false, title = 'Tell me when new towns are published' }) => (
  <AlertForm options={regions} initial={initialRegions} dark={dark} title={title} endpoint="town-alerts" listField="regions" anyField="any_town"
    anyLabel="Any new town" testPrefix="town-alert" subtitle="Pick the regions you care about. One email per new town, unsubscribe any time."
    doneText={(d) => `We'll email you when a new town is published${d.any_town ? ' anywhere in Italy' : ` in ${d.regions.join(', ')}`}.`} />
);

const SERIES_WISHLIST = ['France', 'Spain', 'Portugal', 'Greece', 'Croatia', 'Ireland', 'Other'];

export const CountryAlertForm = ({ exclude = [], dark = false, title = 'Tell me when the Small Towns Series adds a new country' }) => (
  <AlertForm options={SERIES_WISHLIST.filter((c) => !exclude.includes(c))} dark={dark} title={title} endpoint="country-alerts" listField="countries" anyField="any_country"
    anyLabel="Any new country" testPrefix="country-alert" subtitle="Tick the countries you'd love to see next. One email when a new country joins the series."
    doneText={(d) => `We'll email you when the series adds ${d.any_country ? 'a new country' : d.countries.join(', ')}.`} />
);

const DESTINATIONS = ['Spain', 'France', 'Greece', 'Italy', 'Portugal', 'Netherlands', 'Germany', 'Ireland', 'UK', 'Other'];

export const DestinationAlertForm = ({ initial = [], dark = false, title = 'Tell me when new destinations are published' }) => (
  <AlertForm options={DESTINATIONS} initial={initial} dark={dark} title={title} endpoint="destination-alerts" listField="destinations" anyField="any_destination"
    anyLabel="Any new destination" testPrefix="destination-alert" subtitle="Pick the countries you're considering. One email when a guide for that country is published."
    doneText={(d) => `We'll email you when a guide is published for ${d.any_destination ? 'any new destination' : d.destinations.join(', ')}.`} />
);
